import { useEffect, useRef, useState } from "react";
import Meta from "../../components/custom-ui/meta/Meta";
import {
  AcademyButton,
  AcademyCard,
  AcademyImageUpload,
  AcademyModal,
  AcademyStatusPill,
  AcademyTable,
  AcademyTextArea,
  AcademyTextField,
  AcademyToggle,
} from "../../components/academy/settings/AcademySettingsControls";
import SeekerListingPreview from "../../components/academy/settings/SeekerListingPreview";
import {
  AcademyListing,
  formatAcademyDate,
  persistAcademyCover,
  removeAcademyListing,
  saveAcademyListing,
  sortAcademyListings,
  useAcademyListings,
} from "../../data/academyListings";

const BLANK: AcademyListing = {
  id: "",
  order: 0,
  status: "draft",
  kicker: "",
  name: "",
  desc: "",
  meta: "",
  cover: "",
  href: "",
  updated: "",
};

const STATUS_LABEL = { published: "Published", draft: "Draft" };

function quote(name: string) {
  return `“${name}”`;
}

function isValidListing(listing: AcademyListing) {
  return Boolean(listing.name.trim() && /^https?:\/\/.+/.test(listing.href.trim()));
}

function CoverField({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const fileRef = useRef(null);
  const pick = () => {
    const input = fileRef.current as HTMLInputElement | null;
    input?.click();
  };
  const read = (event: { target: HTMLInputElement }) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result || ""));
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  return (
    <div>
      <AcademyImageUpload label="Cover Image" value={value} onClick={pick} />
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={read}
        style={{ display: "none" }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
        <AcademyButton variant="secondary" size="sm" icon="upload" iconPosition="start" onClick={pick}>
          {value ? "Replace" : "Upload"}
        </AcademyButton>
        {value && (
          <AcademyButton variant="ghost" size="sm" icon="x" iconPosition="start" onClick={() => onChange("")}>
            Clear
          </AcademyButton>
        )}
        <span style={{ font: "var(--fw-regular) var(--fs-meta)/1.4 var(--font-body)", color: "var(--text-muted)" }}>
          16:9, at least 960×540
        </span>
      </div>
    </div>
  );
}

function ListingEditor({
  rec,
  saving,
  onClose,
  onSave,
  onPublish,
}: {
  rec: AcademyListing;
  saving: boolean;
  onClose: () => void;
  onSave: (listing: AcademyListing) => void;
  onPublish: (listing: AcademyListing) => void;
}) {
  const draftState = useState(rec);
  const draft = draftState[0] as AcademyListing;
  const setDraft = draftState[1] as (
    next: AcademyListing | ((current: AcademyListing) => AcademyListing),
  ) => void;
  useEffect(() => setDraft(rec), [rec]);
  const set =
    (key: "kicker" | "name" | "desc" | "meta" | "cover" | "href") =>
    (value: string) =>
      setDraft((current: AcademyListing) => ({ ...current, [key]: value }));
  const valid = isValidListing(draft);
  const isNew = !draft.id;

  return (
    <AcademyModal
      open
      width="min(1120px, calc(100vw - 48px))"
      onClose={onClose}
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 14 }}>
          {isNew ? "New Listing" : "Edit Listing"}
          {!isNew && <AcademyStatusPill status={draft.status} label={STATUS_LABEL[draft.status]} />}
        </span>
      }
      actions={
        <>
          <AcademyButton variant="ghost" onClick={onClose}>
            Cancel
          </AcademyButton>
          <AcademyButton
            variant="secondary"
            icon="save"
            iconPosition="start"
            disabled={!valid || saving}
            onClick={() => onSave(draft)}
          >
            Save
          </AcademyButton>
          <AcademyButton
            icon="upload"
            iconPosition="start"
            disabled={!valid || saving}
            onClick={() => onPublish(draft)}
          >
            Publish
          </AcademyButton>
        </>
      }
    >
      <div
        className="acs-editor"
        style={{ maxHeight: "calc(100vh - 260px)", overflowY: "auto", paddingRight: 4 }}
      >
        <div style={{ display: "grid", gap: 16, alignContent: "start" }}>
          <CoverField value={draft.cover} onChange={set("cover")} />
          <AcademyTextField
            label="Kicker"
            value={draft.kicker}
            onChange={set("kicker")}
            placeholder="Certificate scholarship"
            hint="Small uppercase line above the program name."
          />
          <AcademyTextField
            label="Program Name"
            required
            value={draft.name}
            onChange={set("name")}
            placeholder="Grow with Google"
          />
          <AcademyTextArea
            label="Description"
            required
            rows={4}
            maxLength={220}
            value={draft.desc}
            onChange={set("desc")}
            placeholder="What the program covers, who it is for, how long it runs."
          />
          <AcademyTextField
            label="Source Line"
            value={draft.meta}
            onChange={set("meta")}
            placeholder="Application form · Google Forms"
          />
          <AcademyTextField
            label="External URL"
            required
            type="url"
            icon="link"
            value={draft.href}
            onChange={set("href")}
            placeholder="https://"
          />
        </div>
        <div style={{ display: "grid", gap: 14, alignContent: "start" }}>
          <div>
            <div style={{ font: "700 0.8rem/1.2 Lato, system-ui, sans-serif", color: "#183853" }}>
              Preview
            </div>
            <div
              style={{
                font: "400 0.75rem/1.4 Lato, system-ui, sans-serif",
                color: "var(--text-muted)",
                marginTop: 4,
              }}
            >
              Seeker Academy tab
            </div>
          </div>
          <div style={{ background: "var(--surface-sunken)", borderRadius: 12, padding: 20 }}>
            <SeekerListingPreview rec={draft} />
          </div>
          <div style={{ font: "400 0.8rem/1.5 Lato, system-ui, sans-serif", color: "#8a949b" }}>
            Publish puts the tile on the seeker tab immediately. Save keeps the record{" "}
            {isNew ? "as a draft" : "and leaves the live toggle unchanged"}.
          </div>
        </div>
      </div>
    </AcademyModal>
  );
}

function AcademySettingsPage() {
  const { data, isLoading, isError } = useAcademyListings();
  const rowState = useState([] as AcademyListing[]);
  const rows = rowState[0] as AcademyListing[];
  const setRows = rowState[1] as (
    next: AcademyListing[] | ((current: AcademyListing[]) => AcademyListing[]),
  ) => void;
  const editingState = useState(null);
  const editing = editingState[0] as AcademyListing | null;
  const setEditing = editingState[1] as (next: AcademyListing | null) => void;
  const confirmState = useState(null);
  const confirm = confirmState[0] as AcademyListing | null;
  const setConfirm = confirmState[1] as (next: AcademyListing | null) => void;
  const [note, setNote] = useState("");
  const [noteError, setNoteError] = useState(false);
  const [saving, setSaving] = useState(false);
  const noteTimer = useRef(null as number | null);

  useEffect(() => {
    if (Array.isArray(data)) setRows(sortAcademyListings(data));
  }, [data]);

  useEffect(() => {
    return () => {
      if (noteTimer.current) window.clearTimeout(noteTimer.current);
    };
  }, []);

  const flash = (message: string, isErrorNote = false) => {
    setNote(message);
    setNoteError(isErrorNote);
    if (noteTimer.current) window.clearTimeout(noteTimer.current);
    noteTimer.current = window.setTimeout(() => setNote(""), 3200);
  };

  const upsert = async (listing: AcademyListing, status: AcademyListing["status"], message: string) => {
    const id = listing.id || `p${Date.now()}`;
    setSaving(true);
    try {
      const cover = await persistAcademyCover(id, listing.cover);
      const next: AcademyListing = {
        ...listing,
        id,
        cover,
        status,
        updated: formatAcademyDate(),
        order: listing.order || rows.length + 1,
      };
      await saveAcademyListing(next);
      setRows((current: AcademyListing[]) =>
        sortAcademyListings([
          ...current.filter((row: AcademyListing) => row.id !== next.id),
          next,
        ]),
      );
      setEditing(null);
      flash(message);
    } catch {
      flash("Couldn’t save this listing. Please try again.", true);
    } finally {
      setSaving(false);
    }
  };

  const setLive = async (listing: AcademyListing, on: boolean) => {
    const next: AcademyListing = {
      ...listing,
      status: on ? "published" : "draft",
      updated: formatAcademyDate(),
    };
    const previous = rows;
    setRows((current: AcademyListing[]) =>
      current.map((row: AcademyListing) => (row.id === listing.id ? next : row)),
    );
    try {
      await saveAcademyListing(next);
      flash(
        on
          ? `${quote(listing.name)} is live on the seeker Academy tab.`
          : `${quote(listing.name)} pulled from the seeker Academy tab.`,
      );
    } catch {
      setRows(previous);
      flash("Couldn’t update this listing. Please try again.", true);
    }
  };

  const destroy = async (listing: AcademyListing) => {
    const previous = rows;
    setRows((current: AcademyListing[]) =>
      current.filter((row: AcademyListing) => row.id !== listing.id),
    );
    setConfirm(null);
    try {
      await removeAcademyListing(listing.id);
      flash(`${quote(listing.name)} deleted permanently.`);
    } catch {
      setRows(previous);
      flash("Couldn’t delete this listing. Please try again.", true);
    }
  };

  return (
    <div className="academy-settings" data-screen-label="Academy Settings">
      <Meta title="Academy Settings" />
      <style>{`
        .academy-settings {
          --it-navy-800:#183853;
          --it-teal-600:#216478;
          --it-green-500:#58b657;
          --it-grey-100:#eaeff1;
          --it-grey-200:#e3e6e8;
          --it-grey-300:#c6c6c6;
          --it-grey-500:#8a949b;
          --it-danger-500:#d0342c;
          --surface-card:#fff;
          --surface-sunken:var(--it-grey-100);
          --text-heading:var(--it-navy-800);
          --text-body:var(--it-navy-800);
          --text-muted:var(--it-grey-500);
          --border-hairline:var(--it-grey-200);
          --status-published:var(--it-green-500);
          --status-draft:var(--it-grey-500);
          --font-body:Lato,system-ui,sans-serif;
          --font-mono:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;
          --fw-regular:400;
          --fw-semibold:600;
          --fw-bold:700;
          --fs-body:0.8rem;
          --fs-label:0.8rem;
          --fs-meta:0.75rem;
          --lh-body:1.5;
          --ls-tight:-0.01em;
          --text-default:var(--fw-regular) var(--fs-body)/var(--lh-body) var(--font-body);
          --space-5:20px;
          --radius-md:6px;
          --radius-lg:10px;
          --shadow-card:0 1px 3px rgba(24,56,83,.08),0 6px 18px rgba(24,56,83,.06);
          --shadow-menu:0 8px 24px rgba(15,38,55,.18);
          --transition-control:background-color 180ms cubic-bezier(.4,0,.2,1),color 180ms cubic-bezier(.4,0,.2,1),box-shadow 180ms cubic-bezier(.4,0,.2,1);
          display:grid;
          gap:24px;
          align-content:start;
          padding:8px 4px 48px;
          color:var(--text-body);
          font:var(--text-default);
        }
        .academy-settings h2,.academy-settings h3,.academy-settings h4 { letter-spacing:var(--ls-tight); }
        .acs-editor { display:grid; grid-template-columns:minmax(0,1.15fr) minmax(0,.85fr); gap:32px; }
        @media (max-width:900px) { .acs-editor { grid-template-columns:minmax(0,1fr); } }
      `}</style>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24 }}>
        <div>
          <h2 style={{ margin: 0, font: "700 1.563rem/1.2 var(--font-body)", color: "#183853" }}>
            Academy Settings
          </h2>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {note && (
            <span
              style={{
                font: "var(--fw-regular) 0.8rem/1.4 var(--font-body)",
                color: noteError ? "var(--it-danger-500)" : "var(--it-teal-600)",
              }}
            >
              {note}
            </span>
          )}
          <AcademyButton
            icon="plus"
            iconPosition="start"
            onClick={() => setEditing({ ...BLANK, order: rows.length + 1 })}
          >
            New Listing
          </AcademyButton>
        </div>
      </div>

      <AcademyCard title="Academy Listings">
        {isError ? (
          <div style={{ padding: "8px 14px 18px", color: "var(--it-danger-500)" }}>
            Listings could not be loaded. Refresh and try again.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <AcademyTable<AcademyListing>
              style={{ minWidth: 900 }}
              rows={isLoading ? [] : rows}
              columns={[
                {
                  key: "name",
                  label: "Program",
                  width: "minmax(240px,1fr)",
                  render: (row) => (
                    <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
                      <span
                        style={{
                          width: 62,
                          height: 40,
                          flex: "none",
                          borderRadius: 6,
                          border: "1px solid var(--border-hairline)",
                          background: row.cover
                            ? `center/cover no-repeat url(${row.cover})`
                            : "repeating-linear-gradient(135deg,#eef3f7,#eef3f7 6px,#e4ecf3 6px,#e4ecf3 12px)",
                        }}
                      />
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            font: "var(--fw-bold) 1rem/1.3 var(--font-body)",
                            color: "var(--text-heading)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.name}
                        </div>
                        <div
                          style={{
                            font: "var(--fw-regular) var(--fs-meta)/1.3 var(--font-body)",
                            color: "var(--text-muted)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.kicker}
                        </div>
                      </div>
                    </div>
                  ),
                },
                {
                  key: "status",
                  label: "Status",
                  width: "124px",
                  render: (row: AcademyListing) => (
                    <AcademyStatusPill status={row.status} label={STATUS_LABEL[row.status]} />
                  ),
                },
                {
                  key: "live",
                  label: "Live",
                  width: "84px",
                  render: (row: AcademyListing) => (
                    <AcademyToggle checked={row.status === "published"} onChange={(on) => setLive(row, on)} />
                  ),
                },
                { key: "updated", label: "Updated", width: "96px" },
                {
                  key: "actions",
                  label: "",
                  width: "88px",
                  align: "right",
                  render: (row) => (
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                      <AcademyButton size="sm" variant="secondary" icon="pencil" label="Edit" onClick={() => setEditing(row)} />
                      <AcademyButton size="sm" variant="destructive" icon="trash-2" label="Delete" onClick={() => setConfirm(row)} />
                    </div>
                  ),
                },
              ]}
              empty={isLoading ? "Loading listings…" : "No listings. Create one to populate the seeker Academy tab."}
            />
          </div>
        )}
      </AcademyCard>

      {editing && (
        <ListingEditor
          rec={editing}
          saving={saving}
          onClose={() => !saving && setEditing(null)}
          onSave={(listing) =>
            upsert(
              listing,
              listing.id ? listing.status : "draft",
              listing.id
                ? `${quote(listing.name)} saved. Live state unchanged.`
                : `${quote(listing.name)} saved as a draft. Not live.`,
            )
          }
          onPublish={(listing) =>
            upsert(listing, "published", `${quote(listing.name)} published — live on the seeker Academy tab.`)
          }
        />
      )}

      <AcademyModal
        open={!!confirm}
        width={520}
        title="Delete listing?"
        onClose={() => setConfirm(null)}
        actions={
          <>
            <AcademyButton variant="secondary" onClick={() => setConfirm(null)}>
              Cancel
            </AcademyButton>
            <AcademyButton
              variant="destructive"
              icon="trash-2"
              iconPosition="start"
              onClick={() => confirm && destroy(confirm)}
            >
              Delete
            </AcademyButton>
          </>
        }
      >
        {confirm
          ? `This permanently removes ${quote(confirm.name)} and its content. Toggling a listing off keeps it as a draft and it can be set live again; deleted listings cannot be recovered.`
          : ""}
      </AcademyModal>
    </div>
  );
}

export default AcademySettingsPage;
